import { computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { type SFPType, serialize } from '@tig-dpqa-cloud/util-sfp';
import { endOfDay, endOfToday, formatISO, startOfDay, startOfToday, parseISO } from 'date-fns';
import { createInjectable } from 'ngxtension/create-injectable';
import { signalSlice } from 'ngxtension/signal-slice';
import { EMPTY, type Observable, catchError, map, shareReplay, tap } from 'rxjs';
import { injectRumService } from '../rum/rum.service';

import type { shared } from '@tig-dpqa-cloud/contract-backend-common';
import { FilterApiService } from './filter-api.service';
import { union } from 'lodash';

const isVerifiedTag = (tag: string) => tag.startsWith('VERIFIED');
const isNonVerifiedTag = (tag: string) => !tag.startsWith('VERIFIED');

type InitialState = {
  selectedDateRange: [Date, Date];
  status: (shared.SlideScanGeneralQcStatusType | 'NONE')[];
  staining: string[] | undefined;
  selectedFullScannerType: string | undefined;
  selectedScannerType: string | undefined;
  selectedScannerSerial: string | undefined;
  selectedRack: (number | 'unknown')[] | undefined;
  customField1: string[] | undefined;
  searchTerm: string;
  physicalId: string | undefined;
  page: number;
  pageSize: number;
  sortDirection: 'asc' | 'desc';
  selectedTag: string[] | undefined;
};

const initialState: InitialState = {
  status: ['NOT_SET'],
  staining: undefined,
  selectedDateRange: [startOfToday(), endOfToday()],
  selectedFullScannerType: undefined,
  selectedScannerType: undefined,
  selectedScannerSerial: undefined,
  selectedRack: undefined,
  selectedTag: undefined,
  customField1: undefined,
  searchTerm: '',
  physicalId: undefined,
  page: 1,
  pageSize: 50,
  sortDirection: 'desc',
};

const getNullFilter = (arg: string[]) => {
  if (!arg || arg.length === 0) {
    return {};
  }
  const hasUnknownOrEmpty = arg.includes('unknown') || arg.includes('');
  return hasUnknownOrEmpty
    ? { or: { in: Array.from(new Set([...arg.filter((a) => a !== 'unknown'), ''])), isNull: 'true' } }
    : { in: arg };
};

const getGenerateFilterFunction = () => (state: InitialState): SFPType => ({
  and: {
    triggeredAt: {
      and: {
        gt: startOfDay(state.selectedDateRange[0]).toISOString(),
        lt: endOfDay(state.selectedDateRange[1]).toISOString(),
      },
    },
    status: { in: state.status.filter((status) => status !== 'NONE') },
    staining: getNullFilter(state.staining || []),
    customField1: getNullFilter(state.customField1 || []),
    ...(state.selectedTag && { tags: { in: state.selectedTag } }),
    ...(state.selectedScannerType && { scannerType: { eq: state.selectedScannerType } }),
    ...(state.selectedScannerSerial && { scannerSerial: { like: `%${state.selectedScannerSerial}%` } }),
    ...(state.selectedRack && { rackNumber: { in: state.selectedRack.map((r) => r.toString()) } }),
    ...(state.searchTerm && { search: { in: [state.searchTerm] } }),
    ...(state.physicalId && { physicalId: { eq: state.physicalId } }),
  },
  page: state.page,
  pagesize: state.pageSize,
  sort: [{ param: 'triggeredAt', direction: state.sortDirection }],
} satisfies SFPType);

export const FilterService = createInjectable(

  () => {
    const filerApiService = inject(FilterApiService);
    const activatedRoute = inject(ActivatedRoute);
    const rumService = injectRumService();

    const filterInfo$ = filerApiService.getFilterInfoData().pipe(
      shareReplay(1),
      catchError(() => EMPTY),
    );
    const generateFilter = getGenerateFilterFunction();

    const queryParamFilter$: Observable<any> = activatedRoute.queryParamMap.pipe(
      map((params) => {
        const status = params.getAll('status');
        const staining = params.getAll('staining');
        const customField1 = params.getAll('customField1');
        const selectedDateRange = params.getAll('selectedDateRange');
        const selectedFullScannerType = params.get('selectedFullScannerType');
        const selectedScannerType = params.get('selectedScannerType');
        const selectedScannerSerial = params.get('selectedScannerSerial');
        const selectedRack = params.getAll('selectedRack');
        const selectedTag = params.getAll('selectedTag');
        const searchTerm = params.get('searchTerm');
        const physicalId = params.get('physicalId');
        const page = params.get('page');
        const pageSize = params.get('pageSize');

        return {
          ...(status.length && { status }),
          ...(staining && { staining }),
          ...(customField1 && { customField1 }),
          ...(selectedDateRange.length === 2 && {
            selectedDateRange: [parseISO(selectedDateRange[0]), parseISO(selectedDateRange[1])],
          }),
          ...(selectedFullScannerType && { selectedFullScannerType }),
          ...(selectedScannerType && { selectedScannerType }),
          ...(selectedScannerSerial && { selectedScannerSerial }),
          ...(selectedRack.length && { selectedRack: selectedRack.map((r) => Number(r)) }),
          ...(selectedTag.length && { selectedTag }),
          ...(searchTerm && { searchTerm }),
          ...(physicalId && { physicalId }),
          ...(page && { page }),
          ...(pageSize && { pageSize }),
        };
      }),
    );

    const state = signalSlice({
      initialState,
      sources: [queryParamFilter$],
      actionSources: {
        toggleSortDirection: (currentState, toggleSortDirection$: Observable<void>) =>
          toggleSortDirection$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'sort_direction' })),
            map(() => ({ sortDirection: currentState().sortDirection === 'asc' ? 'desc' : 'asc' })),
          ),
        updateSelectedDateRange: (_, update$: Observable<InitialState['selectedDateRange']>) =>
          update$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'date_range' })),
            map((range) => ({ selectedDateRange: [range[0], range[1]], page: 1 })),
          ),
        updateSelectedStatus: (_, status$: Observable<InitialState['status']>) =>
          status$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'status' })),
            map((status) => ({ status, page: 1 })),
          ),
        updateStaining: (_, staining$: Observable<InitialState['staining']>) =>
          staining$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'staining' })),
            map((staining) => ({ staining, page: 1 })),
          ),
        updateSelectedScannerType: (_, scannerType$: Observable<InitialState['selectedScannerType']>) =>
          scannerType$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'scanner_type' })),
            map((scannerType) => {
              if (!scannerType) {
                return {
                  selectedFullScannerType: undefined,
                  selectedScannerType: undefined,
                  selectedScannerSerial: undefined,
                  page: 1,
                };
              }
              const [selectedScannerType, selectedScannerSerial] = scannerType.split(/-(?=[^-]*$)/); // Only split on last occurrence of '-'
              return {
                selectedFullScannerType: scannerType,
                selectedScannerType,
                selectedScannerSerial,
                page: 1,
              };
            }),
          ),
        updateSelectedRack: (_, rack$: Observable<InitialState['selectedRack']>) =>
          rack$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'rack' })),
            map((selectedRack) => ({ selectedRack, page: 1 })),
          ),
        updateSelectedFilterOnTag: (state, tag$: Observable<InitialState['selectedTag']>) =>
          tag$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'tag' })),
            map((selectedFilterOnTag) => ({
              selectedTag: union(state().selectedTag?.filter(isVerifiedTag), selectedFilterOnTag),
              page: 1,
            })),
          ),
        updateSelectedVerifiedTag: (state, tag$: Observable<InitialState['selectedTag']>) =>
          tag$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'tag' })),
            map((selectedVerifiedTag) => ({
              selectedTag: union(state().selectedTag?.filter(isNonVerifiedTag), selectedVerifiedTag),
              page: 1,
            })),
          ),
        updatePage: (_, page$: Observable<InitialState['page']>) => page$.pipe(map((page) => ({ page }))),
        setSearchTerm: (_, searchTerm$: Observable<InitialState['searchTerm']>) =>
          searchTerm$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'search' })),
            map((searchTerm) => ({ searchTerm, page: 1 })),
          ),
        updateCustomField1: (_, customField1$: Observable<InitialState['customField1']>) =>
          customField1$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'custom_field' })),
            map((customField1) => ({ customField1, page: 1 })),
          ),
        updatePhysicalId: (_, physicalId$: Observable<InitialState['physicalId']>) =>
          physicalId$.pipe(
            tap(() => rumService.awsRum()?.recordEvent('dpqa.custom.filter', { type: 'physical_id' })),
            map((physicalId) => ({ physicalId, page: 1 })),
          ),
      },
      selectors: (state) => ({
        selectedVerifiedTag: () => state.selectedTag()?.filter(isVerifiedTag),
        selectedFilterOnTag: () => state.selectedTag()?.filter(isNonVerifiedTag),
      }),
    });

    const filterString = computed(() => serialize(generateFilter(state())));
    const queryParamFilter = computed(() => ({
      ...state(),
      selectedDateRange: state.selectedDateRange().map((range) => formatISO(range, { representation: 'date' })),
    }));

    return {
      sortDirection: state.sortDirection,
      selectedDateRange: state.selectedDateRange,
      status: state.status,
      staining: state.staining,
      selectedScannerType: state.selectedFullScannerType,
      selectedRack: state.selectedRack,
      selectedTag: state.selectedTag,
      selectedFilterOnTag: state.selectedFilterOnTag,
      selectedVerifiedTag: state.selectedVerifiedTag,
      customField1: state.customField1,
      physicalId: state.physicalId,
      toggleSortDirection: () => state.toggleSortDirection(),
      updateSelectedDateRange: (range: InitialState['selectedDateRange']) => state.updateSelectedDateRange(range),
      updateStatus: (status: InitialState['status']) => state.updateSelectedStatus(status),
      updateStaining: (staining: InitialState['staining']) => state.updateStaining(staining),
      updateSelectedScannerType: (scannerType: InitialState['selectedScannerType']) =>
        state.updateSelectedScannerType(scannerType),
      updateSelectedRack: (rack: InitialState['selectedRack']) => state.updateSelectedRack(rack),
      updateSelectedFilterOnTag: (tag: InitialState['selectedTag']) => state.updateSelectedFilterOnTag(tag),
      updateSelectedVerifiedTag: (tag: InitialState['selectedTag']) => state.updateSelectedVerifiedTag(tag),
      updatePage: (page: InitialState['page']) => state.updatePage(page),
      setSearchTerm: (searchTerm: InitialState['searchTerm']) => state.setSearchTerm(searchTerm),
      updateCustomField1: (customField1: InitialState['customField1']) => state.updateCustomField1(customField1),
      updatePhysicalId: (physicalId: InitialState['physicalId']) => state.updatePhysicalId(physicalId),
      filterString,
      filterInfo$,
      searchTerm: state.searchTerm,
      page: state.page,
      pageSize: state.pageSize,
      queryParamFilter,
    };
  },
  { providedIn: 'root' },
);
